import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Popover,
  CircularProgress,
} from '@mui/material';
import { HexColorPicker } from 'react-colorful';
import SaveIcon from '@mui/icons-material/Save';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import QRCodeStyling from 'qr-code-styling';
import { useMakeRequest } from '../../../assets/functions_helper';

// ─── Paleta (heredada de restaurant_location_settings) ───────────────────────
const COLORS = {
  PRIMARY: '#f73541',
  PRIMARY_LIGHT: '#ffeff0',
  PRIMARY_HOVER: '#e62e3a',
  BACKGROUND: '#ffffff',
  TEXT_PRIMARY: '#0f172a',
  TEXT_SECONDARY: '#64748b',
  BORDER: '#e2e8f0',
};

// ─── Opciones de estilo de puntos ────────────────────────────────────────────
const DOT_STYLE_OPTIONS = [
  { value: 'rounded',       label: 'Redondeado' },
  { value: 'dots',          label: 'Puntos' },
  { value: 'classy',        label: 'Clásico' },
  { value: 'classy-rounded',label: 'Clásico Redondeado' },
  { value: 'square',        label: 'Cuadrado' },
  { value: 'extra-rounded', label: 'Extra Redondeado' },
];

const CORNER_STYLE_OPTIONS = [
  { value: 'square',        label: 'Cuadrado' },
  { value: 'dot',           label: 'Redondeado' },
  { value: 'extra-rounded', label: 'Extra Redondeado' },
];

// ─── Componente ─────────────────────────────────────────────────────────────
function QRCodeCustomizer({ restaurantPublicId, restaurantId }) {
  const makeRequest = useMakeRequest();

  // Controles del usuario
  const [dotColor, setDotColor]         = useState('#0f172a');
  const [cornerColor, setCornerColor]   = useState('#f73541');
  const [bgColor, setBgColor]           = useState('#ffffff');
  const [dotStyle, setDotStyle]         = useState('rounded');
  const [cornerStyle, setCornerStyle]   = useState('square');
  const [colorPickerConfig, setColorPickerConfig] = useState({ type: null, anchorEl: null });

  // Estados de carga
  const [saving, setSaving]             = useState(false);
  const [loadingStyles, setLoadingStyles] = useState(false);

  // Instancia de QRCodeStyling
  const qrRef      = useRef(null);
  const qrInstance = useRef(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // URL de demostración (apunta al menú público del restaurante)
  const demoUrl = `${window.location.origin}/restaurantMenu/${restaurantPublicId || 'demo'}`;

  // ─── Carga inicial de estilos desde el backend ────────────────────────────
  useEffect(() => {
    if (!restaurantId) return;
    const token     = localStorage.getItem('token');
    const tokenType = localStorage.getItem('tokenType');
    setLoadingStyles(true);
    makeRequest(
      'GET',
      `restaurant_admin/qr-styles/${restaurantId}`,
      null,
      'application/json',
      token,
      tokenType,
    )
      .then((data) => {
        const styles = data?.styles;
        if (styles && Object.keys(styles).length > 0) {
          if (styles.dot_color)    setDotColor(styles.dot_color);
          if (styles.corner_color) setCornerColor(styles.corner_color);
          if (styles.bg_color)     setBgColor(styles.bg_color);
          if (styles.dot_style)    setDotStyle(styles.dot_style);
          if (styles.corner_style) setCornerStyle(styles.corner_style);
        }
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: 'No se pudieron cargar los estilos guardados.',
          severity: 'warning',
        });
      })
      .finally(() => setLoadingStyles(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  // ─── Crear instancia una sola vez ──────────────────────────────────────────
  useEffect(() => {
    qrInstance.current = new QRCodeStyling({
      width: 280,
      height: 280,
      type: 'svg',
      data: demoUrl,
      margin: 8,
      dotsOptions: {
        color: dotColor,
        type: dotStyle,
      },
      backgroundOptions: {
        color: bgColor,
      },
      cornersSquareOptions: {
        type: cornerStyle,
        color: cornerColor,
      },
      cornersDotOptions: {
        type: 'dot',
        color: cornerColor,
      },
    });

    if (qrRef.current) {
      qrRef.current.innerHTML = '';
      qrInstance.current.append(qrRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Actualizar QR en tiempo real al cambiar opciones ──────────────────────
  useEffect(() => {
    if (!qrInstance.current) return;

    qrInstance.current.update({
      data: demoUrl,
      dotsOptions:        { color: dotColor, type: dotStyle },
      backgroundOptions:  { color: bgColor },
      cornersSquareOptions: { type: cornerStyle, color: cornerColor },
      cornersDotOptions:  { type: 'dot', color: cornerColor },
    });
  }, [dotColor, bgColor, dotStyle, cornerStyle, cornerColor, demoUrl]);

  // ─── Guardar estilos en el backend ─────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!restaurantId) {
      setSnackbar({ open: true, message: 'ID de restaurante no disponible.', severity: 'error' });
      return;
    }
    const token     = localStorage.getItem('token');
    const tokenType = localStorage.getItem('tokenType');
    const styles = {
      dot_color:    dotColor,
      corner_color: cornerColor,
      bg_color:     bgColor,
      dot_style:    dotStyle,
      corner_style: cornerStyle,
    };
    setSaving(true);
    try {
      await makeRequest(
        'PUT',
        `restaurant_admin/qr-styles/${restaurantId}`,
        styles,
        'application/json',
        token,
        tokenType,
      );
      setSnackbar({ open: true, message: '✅ Estilos del QR guardados correctamente.', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: typeof err === 'string' ? err : 'Error al guardar los estilos del QR.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  }, [dotColor, cornerColor, bgColor, dotStyle, cornerStyle, restaurantId, makeRequest]);

  // ─── Estilos de controles ──────────────────────────────────────────────────
  const controlLabelSx = {
    fontWeight: 700,
    color: COLORS.TEXT_PRIMARY,
    fontSize: '0.85rem',
    mb: 0.8,
    fontFamily: 'Como, sans-serif',
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#f8fafc',
      '& fieldset': { borderColor: COLORS.BORDER },
      '&:hover fieldset': { borderColor: COLORS.PRIMARY + '66' },
      '&.Mui-focused fieldset': { borderColor: COLORS.PRIMARY },
    },
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: '20px',
          border: `1px solid ${COLORS.BORDER}`,
          backgroundColor: COLORS.BACKGROUND,
          boxShadow: '0px 10px 30px rgba(0,0,0,0.05)',
        }}
      >
        {/* ── Encabezado ── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 4 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: '16px',
              background: COLORS.PRIMARY_LIGHT,
              color: COLORS.PRIMARY,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <QrCode2RoundedIcon fontSize="medium" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: COLORS.TEXT_PRIMARY, fontFamily: 'Como, sans-serif' }}>
              Personalización de Código QR
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.TEXT_SECONDARY, mt: 0.5, fontFamily: 'Como, sans-serif' }}>
              Diseña un QR único para tu restaurante. Los cambios se ven en tiempo real.
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={4}>

          {/* ══════════════════════════════════════════
              COLUMNA IZQUIERDA: Vista Previa del QR
          ══════════════════════════════════════════ */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              {/* Contenedor del QR con fondo patrón */}
              <Box
                sx={{
                  p: 3,
                  borderRadius: '20px',
                  border: `2px solid ${COLORS.BORDER}`,
                  background: `
                    linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
                    linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
                    linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
                    linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)
                  `,
                  backgroundSize: '20px 20px',
                  backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'fit-content',
                  mx: 'auto',
                  position: 'relative',
                }}
              >
                <Box
                  ref={qrRef}
                  sx={{
                    borderRadius: '14px',
                    overflow: 'hidden',
                    '& svg': { display: 'block' },
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* ══════════════════════════════════════════
              COLUMNA DERECHA: Controles
          ══════════════════════════════════════════ */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* ── Sub-encabezado ── */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, borderBottom: `1px solid ${COLORS.BORDER}` }}>
                <PaletteRoundedIcon sx={{ color: COLORS.PRIMARY, fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.TEXT_PRIMARY, fontFamily: 'Como, sans-serif' }}>
                  Opciones de Diseño
                </Typography>
              </Box>

              {/* ── Colores ── */}
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography sx={controlLabelSx}>Puntos</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      onClick={(e) => setColorPickerConfig({ type: 'dot', anchorEl: e.currentTarget })}
                      sx={{
                        width: 36, height: 36,
                        borderRadius: '10px',
                        border: `2px solid ${COLORS.BORDER}`,
                        backgroundColor: dotColor,
                        cursor: 'pointer',
                        transition: 'transform 0.15s ease',
                        '&:hover': { transform: 'scale(1.08)' },
                      }}
                    />
                    <TextField
                      size="small"
                      value={dotColor}
                      onChange={(e) => setDotColor(e.target.value)}
                      sx={{ ...inputSx, width: 85 }}
                      inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700, fontSize: '0.75rem', padding: '8px' } }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={4}>
                  <Typography sx={controlLabelSx}>Esquinas</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      onClick={(e) => setColorPickerConfig({ type: 'corner', anchorEl: e.currentTarget })}
                      sx={{
                        width: 36, height: 36,
                        borderRadius: '10px',
                        border: `2px solid ${COLORS.BORDER}`,
                        backgroundColor: cornerColor,
                        cursor: 'pointer',
                        transition: 'transform 0.15s ease',
                        '&:hover': { transform: 'scale(1.08)' },
                      }}
                    />
                    <TextField
                      size="small"
                      value={cornerColor}
                      onChange={(e) => setCornerColor(e.target.value)}
                      sx={{ ...inputSx, width: 85 }}
                      inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700, fontSize: '0.75rem', padding: '8px' } }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={4}>
                  <Typography sx={controlLabelSx}>Fondo</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      onClick={(e) => setColorPickerConfig({ type: 'bg', anchorEl: e.currentTarget })}
                      sx={{
                        width: 36, height: 36,
                        borderRadius: '10px',
                        border: `2px solid ${COLORS.BORDER}`,
                        backgroundColor: bgColor,
                        cursor: 'pointer',
                        transition: 'transform 0.15s ease',
                        '&:hover': { transform: 'scale(1.08)' },
                      }}
                    />
                    <TextField
                      size="small"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      sx={{ ...inputSx, width: 85 }}
                      inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700, fontSize: '0.75rem', padding: '8px' } }}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* ── Estilos de forma ── */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Estilo de Puntos</InputLabel>
                    <Select
                      value={dotStyle}
                      label="Estilo de Puntos"
                      onChange={(e) => setDotStyle(e.target.value)}
                    >
                      {DOT_STYLE_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Estilo de Esquinas</InputLabel>
                    <Select
                      value={cornerStyle}
                      label="Estilo de Esquinas"
                      onChange={(e) => setCornerStyle(e.target.value)}
                    >
                      {CORNER_STYLE_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* ── Presets rápidos ── */}
              <Box>
                <Typography sx={controlLabelSx}>Estilos Rápidos</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Clásico', dot: '#000000', corner: '#000000', bg: '#ffffff', style: 'square', cornerStyle: 'square' },
                    { label: 'Moderno', dot: '#0f172a', corner: '#f73541', bg: '#ffffff', style: 'rounded', cornerStyle: 'dot' },
                    { label: 'Elegante', dot: '#1e293b', corner: '#1e293b', bg: '#f8fafc', style: 'classy-rounded', cornerStyle: 'extra-rounded' },
                    { label: 'Marca', dot: COLORS.PRIMARY, corner: COLORS.PRIMARY, bg: '#ffffff', style: 'dots', cornerStyle: 'dot' },
                  ].map((preset) => (
                    <Button
                      key={preset.label}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setDotColor(preset.dot);
                        setCornerColor(preset.corner);
                        setBgColor(preset.bg);
                        setDotStyle(preset.style);
                        setCornerStyle(preset.cornerStyle);
                      }}
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        borderColor: COLORS.BORDER,
                        color: COLORS.TEXT_PRIMARY,
                        fontFamily: 'Como, sans-serif',
                        px: 2,
                        '&:hover': {
                          borderColor: COLORS.PRIMARY,
                          color: COLORS.PRIMARY,
                          backgroundColor: COLORS.PRIMARY_LIGHT,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 14, height: 14,
                          borderRadius: preset.style === 'dots' ? '50%' : '3px',
                          backgroundColor: preset.dot,
                          mr: 0.8,
                          border: `1px solid ${COLORS.BORDER}`,
                        }}
                      />
                      {preset.label}
                    </Button>
                  ))}
                </Box>
              </Box>

              {/* ── Botón guardar ── */}
              <Box sx={{ pt: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || loadingStyles}
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  sx={{
                    backgroundColor: COLORS.PRIMARY,
                    color: '#ffffff',
                    fontWeight: 800,
                    py: 1.6,
                    borderRadius: '16px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontFamily: 'Como, sans-serif',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: COLORS.PRIMARY_HOVER,
                      boxShadow: 'none',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: COLORS.PRIMARY + '80',
                      color: '#ffffff',
                    },
                  }}
                >
                  {saving ? 'Guardando...' : 'Guardar Configuración Visual'}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Popover
        open={Boolean(colorPickerConfig.anchorEl)}
        anchorEl={colorPickerConfig.anchorEl}
        onClose={() => setColorPickerConfig({ type: null, anchorEl: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 2,
            mt: 1,
            borderRadius: '16px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
            border: `1px solid ${COLORS.BORDER}`,
            backgroundColor: '#ffffff',
          }
        }}
      >
        <HexColorPicker
          color={
            colorPickerConfig.type === 'dot' ? dotColor :
            colorPickerConfig.type === 'corner' ? cornerColor :
            bgColor
          }
          onChange={(color) => {
            if (colorPickerConfig.type === 'dot') setDotColor(color);
            if (colorPickerConfig.type === 'corner') setCornerColor(color);
            if (colorPickerConfig.type === 'bg') setBgColor(color);
          }}
        />
      </Popover>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          variant="filled"
          sx={{
            borderRadius: '16px',
            fontFamily: 'Como, sans-serif',
            fontWeight: 700,
            boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default QRCodeCustomizer;
